import {
  CheckCircleOutline,
  Inventory2Outlined,
  PlaceOutlined,
  QrCodeScannerOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PutawayProblemReason,
  PutawayTask,
  claimPutawayTask,
  completePutawayTask,
  reportPutawayProblem,
  scanPutawayDestination,
  scanPutawayHu
} from "./api";
import { SourceTrace } from "./SourceTrace";
import { ScanInput } from "./ScanInput";
import { operationalStatusColor, operationalStatusLabel } from "./operationalStatus";

interface Props {
  task: PutawayTask | null;
  onClose: () => void;
  onChanged: (task: PutawayTask) => void;
  onRefresh: () => void;
  tourMode?: boolean;
}

const problemReasons: PutawayProblemReason[] = [
  "DESTINATION_INACCESSIBLE", "DESTINATION_FULL", "DESTINATION_BLOCKED",
  "DESTINATION_DAMAGED", "PACKAGING_DAMAGED",
  "MATERIAL_DAMAGE_SUSPECTED", "OTHER"
];

function problemActionKey(reason: PutawayProblemReason): string {
  if (reason === "PACKAGING_DAMAGED") {
    return "putaway.actions.createRepackaging";
  }
  if (reason === "MATERIAL_DAMAGE_SUSPECTED" || reason === "HU_DAMAGED") {
    return "putaway.actions.quarantine";
  }
  if (reason === "OTHER") return "putaway.actions.sendForReview";
  return "putaway.actions.searchAnotherLocation";
}

export function PutawayTaskDialog({
  task, onClose, onChanged, onRefresh, tourMode = false
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [current, setCurrent] = useState<PutawayTask | null>(task);
  const [scan, setScan] = useState("");
  const [showProblem, setShowProblem] = useState(false);
  const [reason, setReason] = useState<PutawayProblemReason>(
    "DESTINATION_INACCESSIBLE"
  );
  const [comment, setComment] = useState("");
  const [problemMessage, setProblemMessage] = useState<string | null>(null);
  useEffect(() => {
    if (current?.id === task?.id) return;
    setCurrent(task);
    setScan("");
    setShowProblem(false);
    setComment("");
    setProblemMessage(null);
  }, [task, current?.id]);

  const action = useMutation({
    mutationFn: async (operation: "claim" | "hu" | "bin" | "complete") => {
      if (!current) throw new Error(t("putaway.errors.taskMissing"));
      if (operation === "claim") return claimPutawayTask(current);
      if (operation === "hu") return scanPutawayHu(current, scan);
      if (operation === "bin") {
        const scanned = await scanPutawayDestination(current, scan);
        return tourMode ? completePutawayTask(scanned) : scanned;
      }
      return completePutawayTask(current);
    },
    onSuccess: (updated, operation) => {
      setCurrent(updated);
      setScan("");
      onChanged(updated);
      if (operation === "hu" && !tourMode) {
        onClose();
      }
    }
  });
  const problem = useMutation({
    mutationFn: async () => {
      if (!current) throw new Error(t("putaway.errors.taskMissing"));
      return reportPutawayProblem(current, reason, comment);
    },
    onSuccess: (result) => {
      setProblemMessage(result.message);
      setShowProblem(false);
      setComment("");
      onRefresh();
      if (result.replacementTask) {
        setCurrent(result.replacementTask);
        onChanged(result.replacementTask);
      } else {
        onClose();
      }
    }
  });

  if (!current) return null;
  const assigned = current.status === "ASSIGNED";
  const inProgress = current.status === "IN_PROGRESS";
  const waitingForBin = inProgress && Boolean(current.huScannedAt)
    && !current.destinationScannedAt;
  const readyToComplete = inProgress && Boolean(current.huScannedAt)
    && Boolean(current.destinationScannedAt);

  return (
    <Dialog open fullScreen={fullScreen} fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <QrCodeScannerOutlined color="primary" />
          <Typography variant="h6" fontWeight={800} sx={{ flexGrow: 1 }}>
            Put-away · {current.huNumber}
          </Typography>
          <Chip label={operationalStatusLabel(current.status)}
            color={operationalStatusColor(current.status)} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Alert severity="info" sx={{ display: { xs: "none", sm: "flex" } }}>
            {t("putaway.labels.departure", { source: current.sourcePointCode })} · {t("common.destination")} {current.destinationBinCode} · {t("common.equipment")} {" "}
            {current.assignedEquipmentNumber ?? current.requiredEquipmentTypeCode}
            {current.clientName
              ? ` · ${t("common.client")} ${current.clientName}${current.clientCode
                ? ` (${current.clientCode})` : ""}`
              : ""}
          </Alert>
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <SourceTrace sources={[
              current.asnNumber, current.receiptNumber, current.huNumber
            ]} step={t("putaway.labels.stockedAt", { location: current.destinationBinCode })}
              client={current.clientName ?? current.clientCode} />
          </Box>

          {current.status === "CREATED" && (
            <Alert severity="info" sx={{ display: { xs: "none", sm: "flex" } }}>
              {t("putaway.help.taskCreated")}
            </Alert>
          )}
          {assigned && (
            <Alert severity="warning">
              {tourMode
                ? t("putaway.help.arrived", { location: current.destinationBinCode })
                : t("putaway.help.physicalClaim")}
            </Alert>
          )}
          {inProgress && !current.destinationScannedAt && (
            <Alert severity="success">
              {t("putaway.help.claimed")}
            </Alert>
          )}

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Inventory2Outlined color={current.huScannedAt
              ? "success" : "action"} />
            <Typography sx={{ flexGrow: 1 }}>
              {t("putaway.steps.scanHu")} <strong>{current.huNumber}</strong>
            </Typography>
            {current.huScannedAt && <CheckCircleOutline color="success" />}
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <PlaceOutlined color={current.destinationScannedAt
              ? "success" : "action"} />
            <Typography sx={{ flexGrow: 1 }}>
              {t("putaway.steps.scanLocation")}{" "}
              <strong>{current.destinationBinCode}</strong>
            </Typography>
            {current.destinationScannedAt
              && <CheckCircleOutline color="success" />}
          </Stack>

          {(assigned || waitingForBin) && (
            <ScanInput autoFocus
              testId={assigned ? "putaway-scan-hu" : "putaway-scan-destination"}
              label={assigned ? t("putaway.fields.huCode") : t("putaway.fields.locationCode")}
              value={scan}
              onChange={setScan}
              onSubmit={() => action.mutateAsync(assigned ? "hu" : "bin")}
              submitting={action.isPending}
              submitTestId={assigned
                ? "putaway-confirm-claim" : "putaway-validate-destination"}
              submitLabel={assigned
                ? (tourMode ? t("putaway.actions.scanHu") : t("putaway.actions.confirmClaim"))
                : t("putaway.actions.confirmDeposit")}
              helperText={t("putaway.help.scan")}
            />
          )}

          {action.isError && (
            <Alert severity="error">
              {action.error instanceof Error
                ? action.error.message : t("putaway.errors.action")}
            </Alert>
          )}
          {problemMessage && <Alert severity="success">{problemMessage}</Alert>}

          {showProblem && (
            <Stack spacing={2} sx={{
              p: 2, bgcolor: "error.50", borderRadius: 1
            }}>
              <Typography variant="h6">{t("putaway.actions.reportProblem")}</Typography>
              <FormControl fullWidth>
                <InputLabel id="putaway-problem-reason">{t("putaway.fields.reason")}</InputLabel>
                <Select labelId="putaway-problem-reason" label={t("putaway.fields.reason")}
                  data-testid="putaway-problem-reason"
                  value={reason}
                  onChange={(event) =>
                    setReason(event.target.value as PutawayProblemReason)}>
                  {problemReasons.map((item) => (
                    <MenuItem key={item} value={item}>
                      {t(`putaway.reasons.${item}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth multiline minRows={3}
                data-testid="putaway-problem-comment"
                label={t("putaway.fields.mandatoryComment")}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                helperText={t("putaway.help.problemComment")} />
              {problem.isError && (
                <Alert severity="error">
                  {problem.error instanceof Error
                    ? problem.error.message
                    : t("putaway.errors.problem")}
                </Alert>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button variant="contained" color="error"
                  data-testid="putaway-submit-problem"
                  disabled={comment.trim().length < 3 || problem.isPending}
                  onClick={() => problem.mutate()}>
                  {t(problemActionKey(reason))}
                </Button>
                <Button onClick={() => setShowProblem(false)}>{t("common.actions.cancel")}</Button>
              </Stack>
            </Stack>
          )}

          {!showProblem && current.status === "CREATED" && (
            <Button size="large" variant="contained"
              data-testid="putaway-claim"
              disabled={action.isPending}
              onClick={() => action.mutate("claim")}>
              {t("putaway.actions.claim")}
            </Button>
          )}
          {!showProblem && readyToComplete && (
            <Button size="large" variant="contained" color="success"
              data-testid="putaway-complete"
              disabled={action.isPending}
              onClick={() => action.mutate("complete")}>
              {t("putaway.actions.confirmDeposit")}
            </Button>
          )}
          {current.status === "COMPLETED" && (
            <Alert severity="success">
              {t("putaway.help.completed", { location: current.destinationBinCode })}
            </Alert>
          )}
          {!showProblem
            && !["COMPLETED", "CANCELLED"].includes(current.status) && (
            <Button variant="outlined" color="error"
              data-testid="putaway-report-problem"
              onClick={() => setShowProblem(true)}>
              {t("putaway.actions.reportProblem")}
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.actions.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
